<script>
  import { VIDEO_SERVICE_URL, loadPlayerScripts } from '../static';
  import { External, M } from '../../external';
  import { onMount } from 'svelte';
  import { millisecondsToTimestamp } from '../../Common/tools';

  export let videoElementId = '';
  export let videoSource = '';
  export let videoDuration = 0;
  let embeddedVideoIFrame = '';
  //array of checked checkboxes
  let embeddedOptions = [];
  let isResponsive = true;
  let autoPlayVideo = false;
  let showVideoStartOption = 'all'; // 'autoplay'/'videoloop'/'all' determines if we are showing autoplay or video loop as thumbnail option
  let showMuteVideo = false;
  let showVideoLoopAsThumbnailOpt = false;
  let videoLoopAsThumbStart = 0;
  let videoLoopAsThumbStartHolder = '00:00.00';
  let videoLoopAsThumbEnd = 5000;
  let videoLoopAsThumbEndHolder = '00:05.00';
  let videoLoopAsThumbSlider = null;
  let plyrPreview = null;

  onMount(() => {
    generateIFrame();
  });

  let checkboxAction = (e) => {
    let checked = e.target.checked;
    let value = e.target.value;
    switch (value) {
      case 'videoResponsive':
        isResponsive = checked;
        break;
      case 'autoplay':
        autoPlayVideo = checked;
        showMuteVideo = checked;
        if (checked) {
          showVideoStartOption = 'autoplay';
          showVideoLoopAsThumbnailOpt = false;
          embeddedOptions = embeddedOptions.filter(item => item !== 'videoLoopAsThumbnail');
        } else {
          showVideoStartOption = 'all';
          embeddedOptions = embeddedOptions.filter(item => item !== 'videoMuted');
        }
        break;
      case 'videoLoopAsThumbnail':
        if (checked) {
          autoPlayVideo = false;
          showVideoStartOption = 'videoloop';
          embeddedOptions = embeddedOptions.filter(item => item !== 'autoplay');
        } else {
          showVideoStartOption = 'all';
        }
        showVideoLoopAsThumbnailOpt = checked;
        break;
    }
    if (value !== 'videoResponsive') {
      if (checked) {
        embeddedOptions.push(value);
      } else {
        embeddedOptions = embeddedOptions.filter(item => item !== value);
      }
    }
    generateIFrame();
  };

  let copyToClipboard = () => {
    navigator.clipboard.writeText(embeddedVideoIFrame);
    External.$('#copied').show().delay(500).fadeOut(2000);
  };

  let generateIFrame = () => {
    let src = VIDEO_SERVICE_URL + '/embedded/' + videoElementId + '?';
    for (const embeddedOption of embeddedOptions) {
      if (embeddedOption === 'videoLoopAsThumbnail') {
        src += 'videoLoopAsThumbStart=' + videoLoopAsThumbStart + '&videoLoopAsThumbEnd=' + videoLoopAsThumbEnd + '&videoMuted=true&';
      } else {
        src += embeddedOption + '=true&';
      }
    }
    let responsiveStyle = isResponsive ? ' style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"' : '';
    let iframe = '<iframe ' + responsiveStyle + ' src="' + src + '" width="560" height="320" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>';
    if (isResponsive) {
      embeddedVideoIFrame = '<div style="position: relative;  overflow: hidden; width: 100%; padding-top: 56.25%;">'
        + iframe
        + '</div>';
    } else {
      embeddedVideoIFrame = iframe;
    }
  };

  let loadPlyrPreview = (videoPreviewHolder) => {
    if (showVideoLoopAsThumbnailOpt) {
      loadPlayerScripts(() => {
        plyrPreview = new window.Plyr(videoPreviewHolder, {
          controls: false,
          settings: [],
          clickToPlay: false,
          fullscreen: {
            enabled: false
          }
        });

        if (window.Hls.isSupported()) {
          let hslConfig = {
            //length in seconds
            maxBufferLength: 9,
            //length in seconds
            maxMaxBufferLength: 12,
            //size in bytes
            maxBufferSize: 20 * 1000 * 1000
          };
          let hls = new window.Hls(hslConfig);
          hls.loadSource(videoSource);
          hls.attachMedia(videoPreviewHolder);
        }
        plyrPreview.on('ready', () => {
          plyrPreview.play();
          setTimeout(() => {
            plyrPreview.currentTime = videoLoopAsThumbStart / 1000;
          }, 400);
          plyrPreview.muted = true;
          plyrPreview.volume = 0;
        });
        plyrPreview.on('pause', () => {
          //for some reason Plyr triggers pause event when changing currentTime
          plyrPreview.play();
        });
        plyrPreview.on('timeupdate', event => {
          if (plyrPreview.currentTime >= videoLoopAsThumbEnd / 1000) {
            plyrPreview.currentTime = videoLoopAsThumbStart / 1000;
          }
        });
      });
    }
    return {
      destroy: () => {
        if (plyrPreview) {
          plyrPreview.destroy();
          plyrPreview = null;
        }
      }
    };
  };

  //triggered when slider for video loop as a thumb is shown
  let loadPlyrPreviewSlider = (node) => {
    let sliderHolder = {
      min: 0,
      max: videoDuration,
      value: videoLoopAsThumbStart,
      element: External.$(node),
      onChange: function(val) {
        videoLoopAsThumbStartHolder = millisecondsToTimestamp(val);
      }
    };
    External.Tools.addSlider(sliderHolder);
    sliderHolder.element.on('slide', function(event, ui) {
      videoLoopAsThumbStartHolder = millisecondsToTimestamp(ui.value);
      // Update video player's current time while sliding
      if (plyrPreview) {
        plyrPreview.currentTime = ui.value / 1000;
      }
    });
    sliderHolder.element.on('slidestop', function(event, ui) {
      let selectedVal = ui.value;
      let maxStartVal = videoDuration - 5500;
      if (selectedVal > maxStartVal) {
        selectedVal = maxStartVal;
        this.value = selectedVal;
      }
      setVideoLoopAsThumb(selectedVal, 'start');
    });
    // Store reference to slider for external control
    videoLoopAsThumbSlider = sliderHolder.element;
    return {
      destroy: () => {
        sliderHolder.element.slider('destroy');
        videoLoopAsThumbSlider = null;
      }
    };
  };

  let videoLoopInputChange = e => {
    e.preventDefault();
    let caretPosition = e.target.selectionStart;
    let type = e.target.getAttribute('data-type');
    if (e.target.value.length < 3) {
      e.target.value = '00:00.00';
    }
    if (/^\d$/.test(e.key)) {
      // If the user pressed a number
      if (caretPosition <= 3) {
        caretPosition = 2;
      } else {
        caretPosition = 8;
      }
      let parts = e.target.value.split(':');
      if (parts[0].length === 1) {
        //normalize if minutes were selected
        parts[0] = '0' + parts[0];
      } else if (parts[0].length === 3) {
        //normalize if minutes were selected
        parts[0] = parts[0].slice(1, 3);
      }
      if (parts[1].length === 1) {
        //normalize if seconds were selected
        parts[1] = '00.0' + parts[1];
      } else if (parts[1].length === 6) {
        //normalize if seconds were selected
        parts[1] = parts[1].slice(1, 6);
      }
      let numVal = timeStampToMilliseconds(parts[0] + ':' + parts[1]);
      if (type === 'start') {
        setVideoLoopAsThumb(numVal, type);
      } else {
        e.target.value = millisecondsToTimestamp(numVal);
      }
      e.target.setSelectionRange(caretPosition, caretPosition);
      return;
    } else if (e.key === 'ArrowRight') {
      //select seconds
      e.target.setSelectionRange(3, 8);
      return;
    } else if (e.key === 'ArrowLeft') {
      //select minutes
      e.target.setSelectionRange(0, 2);
      return;
    } else {
      let parts = e.target.value.split(':');
      if (caretPosition < 3) {
        e.target.value = '00:' + parts[1];
      } else {
        e.target.value = parts[0] + ':00.00';
      }
      let numVal = timeStampToMilliseconds(e.target.value);
      setVideoLoopAsThumb(numVal, type);
    }
  };

  let videoLoopInputClick = (e) => {
    const selectionStart = e.target.selectionStart;
    const colonPosition = e.target.value.indexOf(':');
    const dotPosition = e.target.value.indexOf('.');

    if (colonPosition !== -1 && selectionStart > colonPosition) {
      // Clicked on the seconds part
      e.target.setSelectionRange(colonPosition + 1, e.target.value.length);
    } else if (dotPosition !== -1 && selectionStart > dotPosition) {
      // Clicked on the tenths of a second part
      e.target.setSelectionRange(dotPosition + 1, e.target.value.length);
    } else {
      // Clicked on the minutes part
      e.target.setSelectionRange(0, colonPosition);
    }
  };

  let setVideoLoopAsThumb = (val, type) => {
    if (type === 'start') {
      let maxVal = videoDuration - 5500;
      if (val >= maxVal) {
        videoLoopAsThumbStart = maxVal;
        videoLoopAsThumbEnd = maxVal + 5000;
      } else {
        videoLoopAsThumbStart = val;
        if (val > videoLoopAsThumbEnd
          || (videoLoopAsThumbEnd - 5000) > val) {
          videoLoopAsThumbEnd = val + 5000;
        }
      }
    } else {
      if (val > videoDuration
        || val < videoLoopAsThumbStart
        || val > videoLoopAsThumbStart + 5000) {
        val = videoLoopAsThumbStart + 5000;
      }
      videoLoopAsThumbEnd = val;
    }
    videoLoopAsThumbStartHolder = millisecondsToTimestamp(videoLoopAsThumbStart);
    videoLoopAsThumbEndHolder = millisecondsToTimestamp(videoLoopAsThumbEnd);
    videoLoopAsThumbSlider?.slider('value', videoLoopAsThumbStart);
    generateIFrame();
    plyrPreview.currentTime = videoLoopAsThumbStart / 1000;
    if (plyrPreview.paused) {
      //hack as when slider is moved to the end, player pauses because of gremlins :/
      plyrPreview.play();
    }
  };

  let timeStampToMilliseconds = (timestamp) => {
    let parts = timestamp.split(':');
    if (parts.length === 0) {
      return 0;
    }
    let minutes = parseInt(parts[0]);
    let milliseconds = parseInt(parts[1].replace('.', '') + '0');
    return (minutes * 60 * 1000) + milliseconds;
  };

</script>


<div class="popup-widget-wrapper">
  <div class="f-left col9">
    <div class="popup-input-wrapper mb-0">
      <div class="checkbox-wrapper">
        <input value="removeControls" id="removeVideoControls" on:change={checkboxAction}
               class="css-checkbox" name="removeVideoControls" type="checkbox" />
        <label for="removeVideoControls" class="css-label">{M.REMOVE_CONTROLS}</label>
      </div>
    </div>
    {#if showVideoStartOption === 'autoplay' || showVideoStartOption === 'all'}
      <div class="popup-input-wrapper mb-0">
        <div class="checkbox-wrapper">
          <input value="autoplay" id="autoplayVideo" on:change={checkboxAction}
                 class="css-checkbox" name="autoplayVideo" type="checkbox" bind:checked={autoPlayVideo} />
          <label for="autoplayVideo" class="css-label">{M.AUTOPLAY}</label>
        </div>
      </div>
      {#if showMuteVideo}
        <div class="popup-input-wrapper mb-0">
          <div class="checkbox-wrapper">
            <input value="videoMuted" id="videoMuted" on:change={checkboxAction}
                   class="css-checkbox" name="videoMuted" type="checkbox" />
            <label for="videoMuted" class="css-label">
              {M.MUTE_VIDEO}
              <span>{M.MUTE_VIDE_DESC}</span>
            </label>
          </div>
        </div>
      {/if}
    {/if}
    <div class="popup-input-wrapper mb-0">
      <div class="checkbox-wrapper">
        <input value="loop" id="loopVideo" on:change={checkboxAction}
               class="css-checkbox" name="loopVideo" type="checkbox" />
        <label for="loopVideo" class="css-label">{M.LOOP_VIDEO}<span>{M.LOOP_VIDEO_DESC}</span></label>
      </div>
    </div>

    <div class="popup-input-wrapper mb-0">
      <div class="checkbox-wrapper">
        <input value="videoPlayInBackground" id="videoPlayInBackground" on:change={checkboxAction}
               class="css-checkbox" name="videoPlayInBackground" type="checkbox" />
        <label for="videoPlayInBackground" class="css-label">{M.PAUSE_ON_LOST_FOCUS}<span>{M.PAUSE_ON_LOST_FOCUS_DESC}</span></label>
      </div>
    </div>

    <div class="popup-input-wrapper mb-0">
      <div class="checkbox-wrapper">
        <input value="videoResponsive" id="videoResponsive" on:change={checkboxAction}
               class="css-checkbox" name="videoResponsive" type="checkbox" bind:checked={isResponsive} />
        <label for="videoResponsive" class="css-label">{M.RESPONSIVE}<span>{M.RESPONSIVE_DESC}</span></label>
      </div>
    </div>


    {#if showVideoStartOption === 'videoloop' || showVideoStartOption === 'all'}
      <div class="popup-input-wrapper mb-0">
        <div class="checkbox-wrapper">
          <input value="videoLoopAsThumbnail" id="videoLoopAsThumbnail" on:change={checkboxAction}
                 class="css-checkbox" name="videoLoopAsThumbnail" type="checkbox" bind:checked={showVideoLoopAsThumbnailOpt} />
          <label for="videoLoopAsThumbnail" class="css-label">
            {M.USE_VIDEO_THUMB}
            <span>{M.USE_VIDEO_THUMB_DESC}({millisecondsToTimestamp(videoDuration)})</span>
          </label>
        </div>
        {#if showVideoLoopAsThumbnailOpt}
          <div>
            <div class="popup-input-wrapper col9n mb-0">
              <label for="videoLoopAsThumbStart">{M.START}</label>
              <input class="mb-2" type="text" data-type="start"
                     id="videoLoopAsThumbStart" on:keyup={videoLoopInputChange}
                     on:click={videoLoopInputClick} bind:value={videoLoopAsThumbStartHolder} />
            </div>
            <div class="popup-input-wrapper col9n f-right mb-0">
              <label for="videoLoopAsThumbStop">{M.END}</label>
              <input class="mb-3" type="text" data-type="end" id="videoLoopAsThumbStop"
                     on:blur={(e) => {setVideoLoopAsThumb(timeStampToMilliseconds(e.target.value), 'end');}}
                     on:keyup={videoLoopInputChange} on:click={videoLoopInputClick} bind:value={videoLoopAsThumbEndHolder} />
            </div>
            <div use:loadPlyrPreviewSlider></div>
            <div class="popup-input-wrapper mb-1">
              <video crossorigin playsinline use:loadPlyrPreview></video>
            </div>
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <div class="f-right col10">
    <div class="rich-text-area col20 f-left">
      <textarea bind:value={embeddedVideoIFrame} style="resize: none; height: 200px;" readonly />
    </div>
    <button class="middle-button blue rounded-effect mr-5 f-left" on:click={copyToClipboard}>{M.COPY}</button>
    <span id="copied" style="display: none;">
      <svg xmlns="http://www.w3.org/2000/svg" height="48" viewBox="0 -960 960 960" width="48"><path
        d="m421-298 283-283-46-45-237 237-120-120-45 45 165 166Zm59 218q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-156t86-127Q252-817 325-848.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 82-31.5 155T763-197.5q-54 54.5-127 86T480-80Zm0-60q142 0 241-99.5T820-480q0-142-99-241t-241-99q-141 0-240.5 99T140-480q0 141 99.5 240.5T480-140Zm0-340Z" /></svg>
      <span class="copied-text">{M.COPIED}!</span>
    </span>
  </div>

</div>
